import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  memo,
} from "react";
import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
  Avatar,
  AvatarGroup,
  InputAdornment,
  Tooltip,
  Checkbox,
  FormControlLabel,
  useTheme,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useNavigate } from "react-router-dom";
import EditableGrid from "../../components/editableGrid";
import { Meeting, MeetingStatus } from "../../models";
import * as listActions from "../../actions/listActions";
import { Context } from "../../components/context";
import { AppContext } from "../../models/applicationState";
import { ListActions } from "../../actions/listActions";
import { bindActionCreators } from "../../actions/actionCreators";
import { withApplicationInsights } from "../../components/telemetry";
import {
  creatorColumn,
  dateColumn,
  inviteesColumn,
  libraryIsPublishedColumn,
  nameColumn,
} from "../../models/meetingRepresentation";
import {
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  useGridApiRef,
} from "@mui/x-data-grid";
import { EventActions } from "../../actions/eventActions";
import * as eventActions from "../../actions/eventActions";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { TooltipIcon } from "../../components/tooltipIcon";
import GroupAddOutlined from "@mui/icons-material/GroupAddOutlined";
import * as customListActions from "../../actions/listActions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ConfirmationPanel from "../../components/confirmationPanel";
import { ToolbarToggles } from "../../components/toolbarToggles";
import {
  LIBRARY_PAGE_HEADER_HEIGHT,
  LibraryPageHeader,
} from "../../components/libraryPageHeader";
import { generateExportConfig } from "../../util/exportConfigHelper";

// Memoized cell renderer components to prevent re-renders
const CreatorCell = memo(({ creator }: { creator: string }) => (
  <Tooltip title={creator}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: "0.875rem",
          bgcolor: "#242c63",
          color: "#fff",
        }}
      >
        {creator?.charAt(0).toUpperCase() || ""}
      </Avatar>
      {creator}
    </Box>
  </Tooltip>
));

const ParticipantsCell = memo(
  ({ participants }: { participants: string[] }) => {
    const displayParticipants = participants?.slice(0, 5) || [];
    const remainingCount = (participants?.length || 0) - 5;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center"
        }}>
        <Tooltip
          title={participants?.join(", ") || ""}
          slotProps={{
            tooltip: {
              sx: {
                fontSize: "0.8rem",
                maxWidth: 220,
              },
            },
          }}
        >
          <AvatarGroup
            max={6}
            sx={{
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                fontSize: "0.875rem",
              },
            }}
          >
            {displayParticipants.map((participant, index) => (
              <Avatar
                key={index}
                alt={participant}
                sx={{ bgcolor: "#1976d2", color: "#fff" }}
              >
                {participant.charAt(0).toUpperCase()}
              </Avatar>
            ))}
            {remainingCount > 0 && (
              <Avatar
                sx={{
                  bgcolor: "#F3F4F6",
                  color: "#6B7280",
                  fontSize: "0.75rem",
                }}
              >
                +{remainingCount}
              </Avatar>
            )}
          </AvatarGroup>
        </Tooltip>
      </Box>
    );
  },
);

const StatusCell = memo(
  ({ status, t }: { status: MeetingStatus; t: (key: string) => string }) => {
    const isPublished = status === MeetingStatus.Published;
    const isDraft = status === MeetingStatus.Draft;
    const isPlanned = status === MeetingStatus.Planned;
    const isInReview = status === MeetingStatus.InReview;

    const tooltipTitle = isPublished
      ? t("library.status_tooltip.published")
      : isDraft
        ? t("library.status_tooltip.draft")
        : isPlanned
          ? t("library.status_tooltip.planned")
          : isInReview
            ? t("library.status_tooltip.inReview")
            : t("library.status_tooltip.archived");

    return (
      <Tooltip title={tooltipTitle} arrow>
        <Chip
          sx={{ minWidth: "90px", justifyContent: "left", width: "100%" }}
          icon={
            isPublished ? (
              <CheckCircleIcon />
            ) : isPlanned || isDraft ? (
              <AccessTimeIcon />
            ) : isInReview ? (
              <VisibilityIcon />
            ) : undefined
          }
          label={
            isPublished
              ? t("library.published.title")
              : isDraft
                ? t("library.draft")
                : isPlanned
                  ? t("library.planned")
                  : isInReview
                    ? t("library.inReview")
                    : t("library.archived")
          }
          color={
            isPublished
              ? "success"
              : isInReview
                ? "info"
                : isPlanned
                  ? "secondary"
                  : isDraft
                    ? "warning"
                    : "default"
          }
          size="small"
        />
      </Tooltip>
    );
  },
);
const MeetingLibraryPage: React.FC = () => {
  const { t } = useTranslation();
  const MEETING = "meeting";
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const appContext = useContext<AppContext>(Context);
  const theme = useTheme();
  const [showMeetingNavigation, setShowMeetingNavigation] = useState(undefined);
  const { instance } = useMsal();
  const [selectedProjectFilters, setSelectedProjectFilters] = useState(
    appContext.state.selectedProjectFilters,
  );
  const [archivedRows, setArchivedRows] = useState<Meeting[] | null>(null);
  const [optimisticallyRemovedIds, setOptimisticallyRemovedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [showArchived, setShowArchived] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const gridApiRef = useGridApiRef();

  // Export configuration
  const { exportFileName, customLines, printHeader } = generateExportConfig(
    t("library.meetingsLibrary"),
    t,
  );

  // Debounce search input - wait 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Memoize the current username to avoid calling instance.getActiveAccount() repeatedly
  const currentUsername = useMemo(
    () => instance.getActiveAccount()?.username,
    [instance],
  );

  const actions = useMemo(
    () => ({
      lists: bindActionCreators(
        listActions,
        appContext.dispatch,
      ) as unknown as ListActions,
      events: bindActionCreators(
        eventActions,
        appContext.dispatch,
      ) as unknown as EventActions,
    }),
    [appContext.dispatch],
  );

  useEffect(() => {
    if (selectedProjectFilters !== appContext.state.selectedProjectFilters) {
      setSelectedProjectFilters(appContext.state.selectedProjectFilters);
    }
  }, [
    appContext.state.selectedProjectFilters,
    selectedProjectFilters,
    navigate,
  ]);

  const sortMeetings = useCallback((meetings: Meeting[]) => {
    return [...(meetings || [])].sort((a, b) => {
      const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
      const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
      return dateB - dateA;
    });
  }, []);

  // Memoize matchesProjectFilters function
  const matchesProjectFilters = React.useCallback(
    (meeting: Meeting): boolean => {
      if (selectedProjectFilters.tagIds?.length) {
        return selectedProjectFilters.tagIds.some((tagId) =>
          meeting.defaultTags.includes(tagId),
        );
      }
      return selectedProjectFilters?.projectIds.includes(meeting.project);
    },
    [selectedProjectFilters],
  );

  // Derive the base meeting list synchronously to avoid the "raw rows then
  // sorted rows" double-paint flash when the page mounts. Archived rows are
  // fetched lazily into archivedRows; optimistic deletes are tracked via a
  // separate id set so we never need to mirror the source list into state.
  const meetings = useMemo<Meeting[]>(() => {
    const base = showArchived
      ? (archivedRows ?? [])
      : (appContext.state.lists || []).filter(
          (row) => !row.archived && row.status === MeetingStatus.Published,
        );
    if (optimisticallyRemovedIds.size === 0) return base;
    return base.filter((row) => !optimisticallyRemovedIds.has(row.id));
  }, [appContext.state.lists, showArchived, archivedRows, optimisticallyRemovedIds]);

  const gridItems = useMemo(() => {
    let filteredMeetings = meetings;

    if (
      selectedProjectFilters?.projectIds?.length ||
      selectedProjectFilters?.tagIds?.length
    ) {
      filteredMeetings = filteredMeetings.filter((m) =>
        matchesProjectFilters(m),
      );
    }

    // Apply search filter -> for meetingName and userNames (uses debounced value for performance)
    if (debouncedSearchText) {
      const lowerSearchText = debouncedSearchText.toLowerCase();
      filteredMeetings = filteredMeetings.filter(
        (m) =>
          m.name?.toLowerCase().includes(lowerSearchText) ||
          m.participants?.some((p) =>
            p.toLowerCase().includes(lowerSearchText),
          ),
      );
    }

    return sortMeetings(filteredMeetings);
  }, [
    meetings,
    selectedProjectFilters,
    matchesProjectFilters,
    debouncedSearchText,
    sortMeetings,
  ]);

  // Whenever the source list refreshes, drop optimistic-delete overrides so
  // we don't keep filtering rows that the server has now (re)confirmed.
  useEffect(() => {
    setOptimisticallyRemovedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, [appContext.state.lists]);

  // Lazily fetch the archived list when the toggle flips on.
  useEffect(() => {
    let cancelled = false;
    if (showArchived && archivedRows === null) {
      customListActions.getArchived().then((rows) => {
        if (!cancelled) setArchivedRows(rows || []);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [showArchived, archivedRows]);

  const onArchived = useCallback((archived: boolean) => {
    setShowArchived(archived);
  }, []);

  const onEdit = useCallback(
    (meeting: Meeting) => {
      if (meeting) {
        actions.lists.select(meeting);
        navigate(`/meetings/${meeting.id}`);
      }
    },
    [actions.lists, navigate],
  );

  const onReview = useCallback(
    (meeting: Meeting) => {
      if (meeting) {
        actions.lists.select(meeting);
        navigate(`/meetings/${meeting.id}/review`);
      }
    },
    [actions.lists, navigate],
  );

  const onNavigate = useCallback(
    (meeting: Meeting) => {
      if (meeting) {
        actions.lists.select(meeting);
        if (meeting.status === MeetingStatus.Published) {
          navigate(`/library/${meeting.id}`);
        } else {
          setShowMeetingNavigation(meeting.id);
        }
      }
    },
    [actions.lists, navigate],
  );

  const handleMeetingNavigation = useCallback(() => {
    if (showMeetingNavigation) {
      navigate(`/meetings/${showMeetingNavigation}`);
      setShowMeetingNavigation(undefined);
    }
  }, [showMeetingNavigation, navigate]);

  const onDelete = useCallback(
    (meeting: Meeting) => {
      if (meeting) {
        actions.lists.remove(meeting.id);
      }
    },
    [actions.lists],
  );

  const onMeetingUpserted = useCallback(
    async (newMeeting: Meeting, oldMeeting?: Meeting) => {
      // Check if there are actual changes to avoid unnecessary Save API calls
      if (JSON.stringify(newMeeting) !== JSON.stringify(oldMeeting)) {
        try {
          const savedMeeting = await actions.lists.save(newMeeting);
          return savedMeeting;
        } catch (error) {
          console.error("Error saving meeting:", error);
          throw error;
        }
      }
      return newMeeting;
    },
    [actions.lists],
  );

  const handleToggleArchive = useCallback(
    (meeting: Meeting) => {
      if (meeting) {
        const updatedMeeting = { ...meeting, archived: !meeting.archived };
        setOptimisticallyRemovedIds((prev) => {
          const next = new Set(prev);
          next.add(meeting.id);
          return next;
        });
        actions.lists.save(updatedMeeting);
      }
    },
    [actions.lists],
  );

  const gridActions = useCallback(
    (id: GridRowId, row: Meeting) => {
      const isCreator = row.creator === currentUsername;
      const isPublished = row.status === MeetingStatus.Published;

      return [
        <GridActionsCellItem
          key="edit"
          icon={
            <TooltipIcon
              titleKey={"general.edit"}
              icon={<EditOutlinedIcon />}
            />
          }
          label={t("general.edit")}
          onClick={() => onEdit(row)}
          disabled={!isPublished || !isCreator}
          style={{ color: "var(--mui-palette-primary-main)" }}
          showInMenu
        />,
        <GridActionsCellItem
          key="review"
          icon={
            <TooltipIcon
              titleKey={"button.review"}
              icon={<GroupAddOutlined />}
            />
          }
          label={t("button.review")}
          onClick={() => onReview(row)}
          disabled={!isPublished || !isCreator}
          style={{ color: "var(--mui-palette-primary-main)" }}
          showInMenu
        />,
        <GridActionsCellItem
          key="archive"
          icon={
            <TooltipIcon
              titleKey={row.archived ? "general.unarchive" : "general.archive"}
              icon={row.archived ? <UnarchiveIcon /> : <ArchiveOutlinedIcon />}
            />
          }
          disabled={!isCreator}
          label={t(row.archived ? "general.unarchive" : "general.archive")}
          onClick={() => handleToggleArchive(row)}
          style={{ color: "var(--mui-palette-primary-main)" }}
          showInMenu
        />,
      ];
    },
    [currentUsername, onEdit, onReview, handleToggleArchive, t],
  );

  const onCellDoubleClick = useCallback(
    (params) => {
      onNavigate(params.row);
    },
    [onNavigate],
  );

  // Memoize columns to prevent recreating on every render
  const columns = useMemo(
    (): GridColDef[] => [
      {
        field: "open",
        headerName: "",
        width: 50,
        resizable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Tooltip title={t("general.open")}>
              <KeyboardDoubleArrowRightIcon
                onClick={() => onNavigate(params.row)}
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.7 },
                }}
              />
            </Tooltip>
          </Box>
        ),
      },
      {
        ...nameColumn,
        flex: 0.3,
        minWidth: 300,
        editable: true,
      },
      {
        ...dateColumn,
        flex: 0.1,
        minWidth: 120,
        editable: true,
      },
      {
        ...creatorColumn,
        flex: 0.2,
        renderCell: (params) => (
          <CreatorCell creator={params.row.creator || ""} />
        ),
      },
      {
        ...inviteesColumn,
        flex: 0.15,
        minWidth: 170,
        sortable: false,
        filterable: false,
        editable: false,
        renderCell: (params) => (
          <ParticipantsCell participants={params.row.participants} />
        ),
      },
      // {
      //   ...(libraryIsPublishedColumn as GridColDef),
      //   flex: 0.1,
      //   minWidth: 140,
      //   maxWidth: 140,
      //   renderCell: (params) => {
      //     return <StatusCell status={params.row.status} t={t} />;
      //   },
      // },
    ],
    [t, onNavigate],
  );

  return (
    <Box
      sx={{
        pt: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header with title, search, and "new meeting" button */}
      <LibraryPageHeader
        title={t("library.meetingsLibrary")}
        chipLabel={t("library.meeting", { count: gridItems.length })}
        exportProps={{
          apiRef: gridApiRef,
          csvOptions: {
            customLines,
            exportFileName,
          },
          printOptions: {
            printHeader,
            hiddenColumns: [t("item.risk"), t("meeting.actions"), null, "ID"],
          },
        }}
        rightContent={
          <>
            <ToolbarToggles onArchived={onArchived} />
            <TextField
              size="small"
              placeholder={t("library.searchPlaceholder")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: 250,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }} slotProps={{ input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: "text.secondary", fontSize: "1.25rem" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchText("")}
                      edge="end"
                      sx={{ padding: "4px" }}
                    >
                      <ClearIcon sx={{ fontSize: "1.25rem" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              } }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onAuxClick={() => window.open(`/meetings`, "_blank")}
              onClick={() => navigate("/meetings")}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              {t("library.newMeeting")}
            </Button>
          </>
        }
      />

      <ConfirmationPanel
        showModal={showMeetingNavigation}
        hideModal={() => setShowMeetingNavigation(undefined)}
        confirmModal={handleMeetingNavigation}
        action="navigate"
      />

      {/* EditableGrid - columns are memoized to prevent re-renders */}
      <Box sx={{ flexGrow: 1 }}>
        <EditableGrid
          apiRef={gridApiRef}
          height={`calc(90vh - ${LIBRARY_PAGE_HEADER_HEIGHT}px)`}
          columns={columns}
          onDelete={onDelete}
          isDeleteDisabled={(row) => row.creator !== currentUsername}
          isCellEditable={(params) => {
            const isNameOrDate =
              params.field === "name" || params.field === "date";
            const isEditableStatus =
              params.row.status === MeetingStatus.Planned ||
              params.row.status === MeetingStatus.Draft;
            return isNameOrDate && isEditableStatus;
          }}
          extraActions={gridActions}
          processRowUpdate={onMeetingUpserted}
          rows={gridItems}
          actionsHeaderKey=""
          onCellDoubleClick={onCellDoubleClick}
          modelType={MEETING}
          toolbar={() => null}
        />
      </Box>
    </Box>
  );
};

export default withApplicationInsights(
  MeetingLibraryPage,
  "MeetingLibraryPage",
);
